import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Film,
  Headphones,
  Book,
  GraduationCap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { authFetch } from '../utils/authUtils';
import ApprovalNotificationsToaster from '../components/ApprovalNotificationsToaster';
import WriterFollowerChips from '../components/WriterFollowerChips';
import WriterStatsCard from '../components/WriterStatsCard';
import TopTippersCard from '../components/TopTippersCard';
import WriterShoutoutsCard from '../components/WriterShoutoutsCard';
import WritingPathsCard from '../components/WritingPathsCard';
import EvolutionTierOverlay from '../components/EvolutionTierOverlay';
import PriceTagEditor from '../components/PriceTagEditor';
import { resolveCoverUrl } from '../utils/coverUrl';
import EditQuotaPill from '../components/EditQuotaPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  pending: {
    icon: Clock,
    label: 'Pending',
    color: 'text-amber-300',
    bg: 'bg-amber-900/30',
    border: 'border-amber-700/40',
  },
  approved: {
    icon: CheckCircle2,
    label: 'Approved',
    color: 'text-green-300',
    bg: 'bg-green-900/30',
    border: 'border-green-700/40',
  },
  rejected: {
    icon: XCircle,
    label: 'Rejected',
    color: 'text-red-300',
    bg: 'bg-red-900/30',
    border: 'border-red-700/40',
  },
};

const TYPE_META = {
  ebook: { icon: Book, color: 'text-indigo-400', label: 'Ebook' },
  audiobook: { icon: Headphones, color: 'text-purple-400', label: 'Audiobook' },
  cinematic: { icon: Film, color: 'text-pink-400', label: 'Cinematic' },
};

const UploadRow = ({ row, onPriceUpdate }) => {
  const status = STATUS_META[row.status] || STATUS_META.pending;
  const StatusIcon = status.icon;
  const type = TYPE_META[row.content_type] || TYPE_META.ebook;
  const TypeIcon = type.icon;
  const createdDate = row.created_at ? new Date(row.created_at).toLocaleDateString() : '';

  // Feb 12 2026 — Edit allowance pill. Derive the item_kind that the
  // backend uses so the pill can ping `/api/edit-quota/{kind}/{id}`.
  const editKind = (() => {
    if (row.content_type === 'audiobook') {
      return row.kind === 'series' ? 'audiobook_series' : 'audiobook_chapter';
    }
    if (row.content_type === 'cinematic') {
      return row.kind === 'series' ? 'cinematic_series' : 'cinematic_episode';
    }
    return 'content';
  })();

  return (
    <div
      className="flex items-start gap-3 p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg hover:border-slate-600 transition-colors"
      data-testid={`writer-upload-row-${row.id}`}
    >
      {/* Cover / icon */}
      <div className="w-12 h-12 rounded-md bg-slate-700 overflow-hidden flex items-center justify-center flex-shrink-0">
        {row.cover_image ? (
          <img
            src={resolveCoverUrl(row.cover_image)}
            alt={row.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <TypeIcon className={`w-6 h-6 ${type.color}`} />
        )}
      </div>

      {/* Title + meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-white truncate">{row.title}</h3>
          {/* Status badge */}
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color} border ${status.border}`}
            data-testid={`writer-upload-status-${row.id}`}
          >
            <StatusIcon className="w-3 h-3" />
            {status.label}
          </span>
          {/* Kind badge */}
          {row.kind === 'series' && (
            <span className="text-xs text-gray-400 bg-slate-700/50 px-2 py-0.5 rounded">
              Series · {row.episode_count} ep
            </span>
          )}
          {/* Price tag — inline editable */}
          <PriceTagEditor
            contentId={row.id}
            value={row.price_coins}
            onSaved={(next) => onPriceUpdate && onPriceUpdate(row.id, next)}
            testIdPrefix="writer-price"
          />
          {/* Edit allowance pill — Feb 12 2026 user spec */}
          {row.id && (
            <EditQuotaPill
              itemKind={editKind}
              itemId={row.id}
              compact
              testidPrefix={`writer-edit-quota-${row.id}`}
            />
          )}
        </div>
        <p className="text-xs text-gray-400 mt-0.5">
          {type.label}
          {row.genre && ` · ${row.genre}`}
          {createdDate && ` · Uploaded ${createdDate}`}
        </p>
        {row.admin_message && (
          <p className="text-xs text-gray-300 mt-1 italic">
            Admin: "{row.admin_message}"
          </p>
        )}
      </div>
    </div>
  );
};

const WriterDashboard = () => {
  const navigate = useNavigate();
  const [writer, setWriter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ uploads: [], stats: { total: 0, pending: 0, approved: 0, rejected: 0 } });
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingTier, setPendingTier] = useState(null);
  const [writerLevel, setWriterLevel] = useState(1);

  const loadData = useCallback(async () => {
    try {
      // Verify session and role
      const meResponse = await authFetch(`${BACKEND_URL}/api/auth/me`);
      if (!meResponse.ok) {
        navigate('/writer-login');
        return;
      }
      const me = await meResponse.json();
      if (me.role !== 'writer' && me.role !== 'admin') {
        navigate('/writer-login');
        return;
      }
      setWriter(me);

      // Check writer stats — gate assessment + surface pending tier announcement.
      try {
        const sRes = await authFetch(`${BACKEND_URL}/api/writer/stats`);
        if (sRes.ok) {
          const s = await sRes.json();
          // Hard-gate: real scribes who haven't finished assessment go to it.
          // Admins bypass (they may never complete an assessment).
          if (!s.assessmentCompleted && me.role !== 'admin') {
            navigate('/writer-assessment');
            return;
          }
          if (s.pendingTierAnnouncement) {
            setPendingTier(s.pendingTierAnnouncement);
          }
          setWriterLevel(s.writerLevel || 1);
        }
      } catch { /* silent */ }

      const uploadsResponse = await authFetch(`${BACKEND_URL}/api/my-uploads`);
      if (uploadsResponse.ok) {
        const json = await uploadsResponse.json();
        setData(json);
      }
    } catch (err) {
      console.error('Scribe dashboard load error', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  const filteredUploads =
    statusFilter === 'all'
      ? data.uploads
      : data.uploads.filter((u) => u.status === statusFilter);

  return (
    <main className="max-w-7xl mx-auto px-4 py-8" data-testid="writer-dashboard">
      <ApprovalNotificationsToaster />
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">
          Welcome back, {writer?.name || 'Scribe'}
        </h2>
        <p className="text-gray-400">
          Track the status of your submissions and upload new work.
        </p>
      </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="writer-stats">
          <StatCard title="Total Uploads" value={data.stats.total} icon={FileText} color="text-white" />
          <StatCard title="Approved" value={data.stats.approved} icon={CheckCircle2} color="text-green-400" />
          <StatCard title="Pending" value={data.stats.pending} icon={Clock} color="text-amber-400" />
          <StatCard title="Rejected" value={data.stats.rejected} icon={XCircle} color="text-red-400" />
        </div>

        {/* Writer System Phase-1 stats panel (evolution tier, XP, coin balance, 5 core stats) */}
        <div className="mb-8">
          <WriterStatsCard />
        </div>

        {/* Top tippers — auto-hides when no donations yet */}
        <div className="mb-8">
          <TopTippersCard />
        </div>

        {/* Public shoutouts — scribe's own. Hidden in public mode when empty. */}
        <div className="mb-8">
          <WriterShoutoutsCard mine />
        </div>

        {/* Phase-2 Writing Paths panel — only shows once paths exist */}
        <div className="mb-8">
          <WritingPathsCard currentLevel={writerLevel} />
        </div>

        {pendingTier && (
          <EvolutionTierOverlay
            announcement={pendingTier}
            onClose={() => setPendingTier(null)}
          />
        )}

        {/* Followers per series — fanbase visibility */}
        <WriterFollowerChips />

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <ActionCard
            icon={Upload}
            title="Upload Content"
            desc="Ebooks, audiobooks, or single cinematics"
            gradient="from-purple-600 to-pink-600"
            onClick={() => navigate('/upload')}
            testId="writer-action-upload"
          />
          {/* Cinematic + Audiobook series authoring cards removed
              Feb 28 2026 per user instruction. */}
        </div>

        {/* Scribe Training CTA — drives scribes into craft lessons that pay cashable coins */}
        <button
          onClick={() => navigate('/writer-courses')}
          className="w-full mb-8 group flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-br from-fuchsia-600/20 via-pink-600/10 to-rose-600/20 border border-fuchsia-500/30 hover:border-fuchsia-400/60 transition-all text-left"
          data-testid="writer-cta-courses"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-400 to-rose-500 flex items-center justify-center shrink-0 shadow-lg shadow-fuchsia-500/20">
            <GraduationCap className="w-6 h-6 text-slate-900" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest text-fuchsia-300 mb-0.5">
              Sharpen the craft
            </p>
            <p className="text-lg font-semibold text-white">Scribe Training</p>
            <p className="text-xs text-gray-400 mt-0.5">
              4 tracks · 20 short lessons · earn Scribe XP & cashable coins
            </p>
          </div>
        </button>

        {/* My Uploads Section */}
        <Card className="bg-slate-800/50 border-slate-700" data-testid="writer-my-uploads">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-white">My Uploads</CardTitle>
            <div className="flex flex-wrap gap-1">
              {['all', 'pending', 'approved', 'rejected'].map((key) => (
                <Button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  variant={statusFilter === key ? 'default' : 'outline'}
                  size="sm"
                  className={
                    statusFilter === key
                      ? 'bg-indigo-600 hover:bg-indigo-700 h-7 text-xs'
                      : 'bg-transparent border-slate-600 text-gray-300 hover:bg-slate-700 h-7 text-xs'
                  }
                  data-testid={`writer-filter-${key}`}
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {filteredUploads.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="writer-uploads-empty">
                {statusFilter === 'all'
                  ? "You haven't uploaded anything yet. Use the actions above to get started."
                  : `No ${statusFilter} uploads.`}
              </div>
            ) : (
              filteredUploads.map((row) => (
                <UploadRow
                  key={row.id}
                  row={row}
                  onPriceUpdate={(id, next) =>
                    setData((prev) => ({
                      ...prev,
                      uploads: prev.uploads.map((u) =>
                        u.id === id ? { ...u, price_coins: next } : u
                      ),
                    }))
                  }
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Admin Note */}
        {writer?.role === 'admin' && (
          <div className="mt-8 bg-purple-900/20 border border-purple-700/30 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-purple-300 mb-1">Admin Access</h3>
                <p className="text-purple-200 text-sm">
                  You have admin privileges. You can manage all content and users.
                </p>
              </div>
              <Button
                onClick={() => navigate('/admin')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Go to Admin Dashboard
              </Button>
            </div>
          </div>
        )}
    </main>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </CardContent>
  </Card>
);

const ActionCard = ({ icon: Icon, title, desc, gradient, onClick, testId }) => (
  <Card className={`bg-gradient-to-br ${gradient} border-0`}>
    <CardContent className="p-5">
      <Icon className="w-10 h-10 text-white mb-3" />
      <h3 className="text-lg font-bold text-white mb-1">{title}</h3>
      <p className="text-white/80 mb-3 text-sm">{desc}</p>
      <Button
        onClick={onClick}
        className="bg-white text-slate-900 hover:bg-gray-100 text-sm"
        data-testid={testId}
      >
        Open
      </Button>
    </CardContent>
  </Card>
);

export default WriterDashboard;
